import { engineComponent, ArrayUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ImageElement, ImageElementAttributes } from "../Components/Elements/Graphics/ImageElement";
import { Tweens, TweensAttributes } from "../Components/Effects/Tweens";
import { Attributes } from "../Utils/Attributes";
import { ShapeElement } from "../Components/Elements/Graphics/ShapeElement";
import { StageController } from "../Components/StageController";
import { CustomUtils } from "../Utils/CustomUtils";

/**
 * ImageElement class is class which creates holder for image from resources. This image will be rendered to canvas.
 * Example:
 *   <ImageElement Class="ImageElement" Image="[imageId]" X="0" Y="0" />
 * @class ImageElement
 * @extends BaseElement
 */

@engineComponent(ComponentTypes.DisplayObject)
export class ImageTintElement extends ImageElement {
    private _tintTween: number;

    public updateByNode(node: Element): ImageTintElement {
        if (!node) {
            this.logError("No node specified for element update!");
            return;
        }

        if (DEBUG) {
            if (
                node.hasAttribute(ImageElementAttributes.DEBUG) &&
                node.getAttribute(ImageElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        if (node.hasAttribute(ImageTintElementAttributes.REMOVE_TWEENS)) {
            Tweens.removeTweens(this);
        } else {
            Tweens.removeTweens(this, "_tintTween");
        }

        this.visible = Attributes.assignBooleanAttribute(node, ImageElementAttributes.VISIBLE, this.visible);
        this.alpha = Attributes.assignFloatAttribute(node, ImageElementAttributes.ALPHA, this.alpha);
        this.blendMode = CustomUtils.getBlendMode(
            this.getBlendModeByString(
                Attributes.assignStringAttribute(node, ImageElementAttributes.BLEND_MODE, this.blendMode),
            ),
        );
        this.pivot.x = Attributes.assignIntAttribute(node, ImageElementAttributes.PIVOT_X, this.pivot.x);
        this.pivot.y = Attributes.assignIntAttribute(node, ImageElementAttributes.PIVOT_Y, this.pivot.y);

        if (node.hasAttribute(ImageElementAttributes.ROTATION)) {
            this.rotation = this.degrees2radians(parseFloat(node.getAttribute(ImageElementAttributes.ROTATION)));
        }

        if (node.hasAttribute(ImageElementAttributes.START_ROTATION)) {
            if (node.getAttribute(ImageElementAttributes.START_ROTATION) === ImageElementAttributes.START_ROTATION) {
                this.rotation = Math.floor(Math.random() * 360) / 100;
            }
        }

        this.position.x = Attributes.assignIntAttribute(node, ImageElementAttributes.X, this.position.x);
        this.position.y = Attributes.assignIntAttribute(node, ImageElementAttributes.Y, this.position.y);

        if (node.hasAttribute(ImageElementAttributes.SCALE_X)) {
            this.scale.x = parseFloat(node.getAttribute(ImageElementAttributes.SCALE_X));
        }

        if (node.hasAttribute(ImageElementAttributes.SCALE_Y)) {
            this.scale.y = parseFloat(node.getAttribute(ImageElementAttributes.SCALE_Y));
        }

        if (node.hasAttribute(ImageElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(ImageElementAttributes.SCALE));
            this.scale.y = parseFloat(node.getAttribute(ImageElementAttributes.SCALE));
        }

        if (node.hasAttribute(ImageElementAttributes.MASK)) {
            let next: Element = node.parentElement.firstElementChild;
            while (next != null) {
                if (next.nodeName === node.getAttribute(ImageElementAttributes.MASK)) {
                    let shapeElement: ShapeElement = new ShapeElement();
                    shapeElement.create(next);
                    this.mask = shapeElement;
                }
                next = next.nextElementSibling;
            }

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute(ImageElementAttributes.MASK));
                }
            }
        }

        if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK)) {
            let image = StageController.getTextureByResourceId(node.getAttribute(ImageElementAttributes.BITMAP_MASK));

            if (image != null) {
                this.maskSprite = new PIXI.Sprite(image);
                if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK_X)) {
                    this.maskSprite.position.x = parseFloat(node.getAttribute(ImageElementAttributes.BITMAP_MASK_X));
                }
                if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK_Y)) {
                    this.maskSprite.position.y = parseFloat(node.getAttribute(ImageElementAttributes.BITMAP_MASK_Y));
                }
                if (!node.hasAttribute(ImageElementAttributes.BITMAP_MASK_SHOW)) {
                    this.addChild(this.maskSprite);
                    this.mask = this.maskSprite;
                }
            } else {
                if (DEBUG) {
                    this.log(
                        "ERROR" +
                            this.componentName +
                            " no image with name for mask: " +
                            node.getAttribute(ImageElementAttributes.BITMAP_MASK),
                    );
                }
            }
        }

        if (node.hasAttribute(ImageElementAttributes.TINT)) {
            let spr: PIXI.Sprite;
            if (node.getAttribute(ImageElementAttributes.TINT) === "0") {
                this.tint = null;
            } else if (node.getAttribute(ImageElementAttributes.TINT) === "dynamic") {
                this.tint = parseInt(ImageElement.DynamicTint, 16);
            } else {
                this.tint = parseInt(node.getAttribute(ImageElementAttributes.TINT), 16);
                if (this.children != null && this.children[0] != null && this.children[0] instanceof PIXI.Sprite) {
                    spr = <PIXI.Sprite>this.children[0];
                    spr.tint = this.tint;
                }
            }
        }

        this.runPositionTween(node);
        this.runScaleTween(node);
        this.runRotationTween(node);
        this.runAlphaTween(node);
        this.runTween(node);
        this.runTintTween(node);

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }

        return this;
    }

    public runTintTween(node: Element): boolean {
        if (node.hasAttribute(ImageTintElementAttributes.TINT_TWEEN)) {
            let i = 0;
            let tints = ArrayUtils.stringArray(node.getAttribute(ImageTintElementAttributes.TINT_TWEEN));
            let tint = tints[tints.length - 1];

            if (tints.length == 2) {
                this.tint = parseInt(tints[0], 16);
            }

            /*if(this.tint === parseInt(tint,16)) {
                return;
            }*/

            let finalTint: Array<number> = [
                parseInt(tint.slice(0, 2), 16),
                parseInt(tint.slice(2, 4), 16),
                parseInt(tint.slice(4, 6), 16),
            ];
            tint = this.zerosPad(this.tint.toString(16), 6);
            let currentTint: Array<number> = [
                parseInt(tint.slice(0, 2), 16),
                parseInt(tint.slice(2, 4), 16),
                parseInt(tint.slice(4, 6), 16),
            ];

            this._tintTween = 0;
            const tween = "_tintTween";
            const tweenValues: Array<number> = [0, 1];
            const tweenDuration: Array<number> = ArrayUtils.numberArray(
                node.getAttribute(TweensAttributes.TWEEN_DURATION + i) || "120",
            );
            const tweenEase: Array<string> = ArrayUtils.stringArray(
                node.getAttribute(TweensAttributes.TWEEN_EASE + i) || "linear",
            );
            const tweenDelay: Array<number> = ArrayUtils.numberArray(
                node.getAttribute(TweensAttributes.TWEEN_DELAY + i) || "0",
            );
            const tweenLoop: boolean = Attributes.assignBooleanAttribute(node, TweensAttributes.TWEEN_LOOP + i, null);
            let customTween = Tweens.customTween(this, {
                tween,
                tweenValues,
                tweenEase,
                tweenDuration,
                tweenDelay,
                tweenLoop,
            });

            customTween.eventCallback("onUpdate", this.onTweenUpdate, [currentTint, finalTint], this);
        }

        return false;
    }

    private onTweenUpdate(currentTint: Array<number>, finalTint: Array<number>): void {
        let updateTint: Array<string> = [];

        for (let i = 0; i < currentTint.length; i++) {
            let diff = 0;

            if (currentTint[i] > finalTint[i]) {
                diff = Math.round(currentTint[i] - (currentTint[i] - finalTint[i]) * this._tintTween);
            } else {
                diff = Math.round(currentTint[i] + (finalTint[i] - currentTint[i]) * this._tintTween);
            }

            diff = diff > 255 ? diff % 255 : diff;
            diff = Math.abs(diff);

            let hex = diff.toString(16);
            hex = hex.length === 1 ? "0" + hex : hex;
            updateTint.push(hex);
        }

        this.tint = parseInt(updateTint.join(""), 16);
    }

    private zerosPad(num: string, size: number): string {
        let s = num;
        while (s.length < size) s = "0" + s;
        return s;
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return ImageElement
     */
    clone(): ImageTintElement {
        let element: ImageTintElement = new ImageTintElement();
        element.removeChildren();

        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.anchor = this.anchor;
        element.tint = this.tint;
        element.blendMode = this.blendMode;
        element.name = this.name;

        if (this.texture != null) {
            element.texture = this.texture;
        }

        if (this.maskSprite != null) {
            let elementMask = new PIXI.Sprite((<PIXI.Sprite>this.maskSprite).texture);
            elementMask.position.x = this.maskSprite.position.x;
            elementMask.position.y = this.maskSprite.position.y;

            element.maskSprite = elementMask;
            element.addChild(elementMask);
            element.mask = elementMask;
        }

        element.markAsCloned(); // must be called last before return!

        return element;
    }
}

export const ImageTintElementAttributes = {
    TINT_TWEEN: "TintTween",
    REMOVE_TWEENS: "RemoveTweens",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ImageTintElement.ts
 **/